/**
 * Shared add-to-cart feedback + guarded-add helpers (window.iwAtc).
 *
 * Framework-agnostic (vanilla DOM), so every ATC entry point can share one
 * implementation of the button state machine and the in-flight lock. Consumers
 * today: the PDP #add button (snippets/product_linked-options.liquid) and the
 * sticky ATC (assets/product-sticky-atc.js). Loaded early in scripts_head so it
 * is defined before any inline PDP handler runs.
 *
 * API:
 *   iwAtc.setButtonState(button, 'adding' | 'added' | 'reset')
 *     Swaps the button label to Adding…/Added ✓ (spinner + success come from
 *     the .is-adding/.is-added CSS in custom.css), and toggles aria-busy /
 *     aria-disabled — never native `disabled`, so the button stays in the
 *     accessibility tree and screen readers announce the state change. The
 *     original markup (label span, price, arrow) is stored and restored on
 *     reset, so it works for buttons with or without an inner label element.
 *
 *   iwAtc.guardedAdd(button, run) -> Promise
 *     Per-button in-flight lock (a data-iw-adding flag on the element). No-ops
 *     if an add is already running on that button. Sets 'adding', awaits run(),
 *     then 'added' (brief flourish, auto-reset) on success or 'reset' on
 *     failure. Always releases the lock in a finally tied to run()'s real
 *     lifecycle. `run` owns all user-facing messaging; it must reject only when
 *     the item was NOT added (showAddedToCart swallows drawer failures inside
 *     run, so a drawer that didn't redraw still confirms the add).
 *
 *   iwAtc.fetchJsonWithTimeout(url, options, ms) -> Promise<{response, data}>
 *     fetch() + JSON parse under one AbortController timeout (covers the body
 *     read, not just headers). A missing or non-positive ms falls back to 10s,
 *     so a call can never abort before it starts. Rejects on a non-2xx status
 *     or an unreadable body with an Error carrying `response` and `data` (null
 *     when unreadable), the way $.ajax rejects, so fetch and jQuery adds share
 *     one failure path.
 *
 *   iwAtc.addBackstopMs
 *     The time limit for /cart/add.js, on the fetch and jQuery paths alike.
 *     Abandoning an add only discards Shopify's answer — the add can still be
 *     applied — so the limit is long enough that only a connection that has
 *     stopped delivering reaches it, and reaching it runs the cart check
 *     (confirmAddLanded) rather than reporting a failure. It exists so the
 *     button always unlocks; "Still adding…" shows after 10s.
 *
 *   iwAtc.showFailureMessage(failure)
 *     Renders why an add failed into #cart-message, replacing the theme's
 *     showCartErrorMessage, which called every failure a stock-out — so a
 *     dropped connection told shoppers the product was gone. Takes the failure
 *     as thrown (a jqXHR or an Error, with or without `response`) and picks
 *     the wording from Theme settings → Cart via window.iwCartMessages.
 *
 *   iwAtc.shopifyAnswered(failure) -> boolean
 *     Whether Shopify gave a definite answer to the add (a 422, say), as
 *     opposed to one whose outcome is unknown and needs reconciling.
 *
 *   iwAtc.cartUnitsKnown() -> Promise<number|null>
 *     The cart's unit count before an add, for use as its baseline. Waits out
 *     a drawer redraw still in flight from the previous add, and reads the cart
 *     fresh when that redraw couldn't.
 *
 *   iwAtc.confirmAddLanded(failure, unitsBefore, qty) -> Promise<cart|null>
 *     Reconciles an add whose request never returned a status line against the
 *     real cart — read twice, 3s apart — so a dropped connection that still
 *     delivered the add is reported as the success it was. Callers hand the
 *     cart it read to the drawer refresh, where it serves as the rebuild's
 *     fallback copy.
 *
 *   iwAtc.readCart() -> Promise<cart|null>
 *     The cart as the server has it, or null when it can't be read.
 *
 *   iwAtc.resyncCartUi() -> Promise
 *     Redraws the drawer and progress bar from that cart, for the paths that
 *     end in a message rather than a confirmed add. Not awaited: a refused add
 *     created nothing to double-add, so the button needn't wait on a repaint to
 *     report the refusal.
 *
 *   iwAtc.showAddedToCart(cart) -> Promise
 *     The success message, the drawer opening and its redraw, for an add that
 *     landed. Not awaited: the add is confirmed once Shopify accepts it, so the
 *     button reports that without holding its lock across the redraw's own
 *     round trips.
 */
(function () {
  if (window.iwAtc) return;

  var ADDING_LABEL = 'Adding…';
  var SLOW_LABEL = 'Still adding…';
  var ADDED_LABEL = 'Added ✓';
  var ADDED_HOLD_MS = 1800;
  var CART_READ_TIMEOUT_MS = 10000;
  var RECOVERY_READS = 2;
  var RECOVERY_RECHECK_MS = 3000;
  var SLOW_NOTICE_MS = 10000;
  var MESSAGE_HOLD_MS = 5000;
  var OUTCOME_UNKNOWN_STATUSES = [502, 503, 504];
  var addBackstopMs = 150000;

  // Used when the theme settings haven't rendered into the page, so a failed
  // add always says something.
  var FALLBACK_MESSAGES = {
    network: "We couldn't reach the cart. Check your connection and try again.",
    unavailable: 'Sorry! Seems like the product is out of stock',
    generic: 'Something went wrong adding this to your cart. Please try again.',
  };

  var ADDING_ANNOUNCE = 'Adding to cart';
  var SLOW_ANNOUNCE = 'Still adding to cart';
  var ADDED_ANNOUNCE = 'Added to cart';
  var ERROR_ANNOUNCE = 'Unable to add to cart';

  var pendingRedraw = Promise.resolve();
  var cartObjectStale = false;

  var liveRegion = null;
  var announceTimer = null;
  function ensureLiveRegion() {
    if (liveRegion) return liveRegion;
    liveRegion = document.createElement('div');
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.style.cssText =
      'position:absolute;width:1px;height:1px;margin:-1px;padding:0;' +
      'overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);' +
      'white-space:nowrap;border:0;';
    document.body.appendChild(liveRegion);
    return liveRegion;
  }

  function announce(message) {
    if (!message) return;
    var region = ensureLiveRegion();
    clearTimeout(announceTimer);
    region.textContent = '';
    announceTimer = setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  // Normalizes the shapes a failed add arrives in — a jqXHR, or an Error that
  // may carry the fetch `response` — down to an HTTP status, or 0 when the
  // add's outcome is unknown. That covers a transport failure that never
  // reached a status line (offline, reset, DNS, the add backstop), a 2xx whose
  // body couldn't be read (the add was accepted; only the answer was lost), and
  // a gateway error, which Shopify's edge can return after the add was applied.
  function failureStatus(failure) {
    var status = 0;
    if (failure && failure.response) status = failure.response.status || 0;
    else if (failure && typeof failure.status === 'number') status = failure.status;
    var outcomeUnknown =
      (status >= 200 && status < 300) || OUTCOME_UNKNOWN_STATUSES.indexOf(status) !== -1;
    return outcomeUnknown ? 0 : status;
  }

  function shopifyAnswered(failure) {
    return failureStatus(failure) !== 0;
  }

  // Shopify's own wording for a rejected item ("You can only add 1 of X to your
  // cart"), which is always more specific than the configured fallback.
  function shopifyReason(failure) {
    var body = failure && (failure.data || failure.responseJSON);
    return (body && body.description) || '';
  }

  var messageTimer = null;

  function showFailureMessage(failure) {
    var status = failureStatus(failure);
    var configured = window.iwCartMessages || {};
    var text;
    if (status === 0) {
      text = configured.network || FALLBACK_MESSAGES.network;
    } else if (status === 422) {
      text =
        shopifyReason(failure) ||
        configured.unavailable ||
        FALLBACK_MESSAGES.unavailable;
    } else {
      text = configured.generic || FALLBACK_MESSAGES.generic;
    }

    var el = document.getElementById('cart-message');
    if (!el) return;
    el.classList.remove('message-success');
    el.classList.add('message-error');
    el.textContent = text;
    el.style.display = 'block';
    clearTimeout(messageTimer);
    messageTimer = setTimeout(function () {
      el.style.display = 'none';
    }, MESSAGE_HOLD_MS);
  }

  function labelMarkup(text) {
    var span = document.createElement('span');
    span.className = 'iw-atc-label';
    span.textContent = text;
    return span.outerHTML;
  }

  // Swaps only the visible label, leaving the stored original markup and the
  // button's state classes alone, so it is safe to call mid-add.
  function setLabel(button, text) {
    if (!button) return;
    button.innerHTML = labelMarkup(text);
  }

  function setButtonState(button, state) {
    if (!button) return;
    if (state === 'adding') {
      if (button.dataset.iwLabel == null) button.dataset.iwLabel = button.innerHTML;
      button.classList.add('is-adding');
      button.classList.remove('is-added');
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      setLabel(button, ADDING_LABEL);
    } else if (state === 'added') {
      button.classList.remove('is-adding');
      button.classList.add('is-added');
      button.setAttribute('aria-busy', 'false');
      button.removeAttribute('aria-disabled');
      setLabel(button, ADDED_LABEL);
    } else {
      button.classList.remove('is-adding');
      button.classList.remove('is-added');
      button.setAttribute('aria-busy', 'false');
      button.removeAttribute('aria-disabled');
      if (button.dataset.iwLabel != null) {
        button.innerHTML = button.dataset.iwLabel;
        delete button.dataset.iwLabel;
      }
    }
  }

  var resetTimers = new WeakMap();

  function guardedAdd(button, run) {
    if (button && button.dataset.iwAdding) return Promise.resolve();
    if (button) {
      button.dataset.iwAdding = '1';
      if (resetTimers.has(button)) {
        clearTimeout(resetTimers.get(button));
        resetTimers.delete(button);
      }
    }
    setButtonState(button, 'adding');
    announce(ADDING_ANNOUNCE);
    var slowTimer = setTimeout(function () {
      if (button && button.dataset.iwAdding) {
        setLabel(button, SLOW_LABEL);
        announce(SLOW_ANNOUNCE);
      }
    }, SLOW_NOTICE_MS);
    return Promise.resolve()
      .then(run)
      .then(
        function () {
          setButtonState(button, 'added');
          announce(ADDED_ANNOUNCE);
          if (button) {
            resetTimers.set(
              button,
              setTimeout(function () {
                resetTimers.delete(button);
                setButtonState(button, 'reset');
              }, ADDED_HOLD_MS)
            );
          }
        },
        function (err) {
          console.warn('ATC add failed', err);
          setButtonState(button, 'reset');
          announce(ERROR_ANNOUNCE);
        }
      )
      .finally(function () {
        clearTimeout(slowTimer);
        if (button) delete button.dataset.iwAdding;
      });
  }

  async function fetchJson(url, options) {
    var response = await fetch(url, options || {});
    var data = null;
    try {
      data = await response.json();
    } catch (parseErr) {
      if (parseErr && parseErr.name === 'AbortError') throw parseErr;
    }
    if (!response.ok || data === null) {
      var failure = new Error(
        url + ' answered ' + response.status + (data === null ? ' with an unreadable body' : '')
      );
      failure.response = response;
      failure.data = data;
      throw failure;
    }
    return { response: response, data: data };
  }

  async function fetchJsonWithTimeout(url, options, ms) {
    var controller = new AbortController();
    var timer = setTimeout(function () {
      controller.abort();
    }, ms > 0 ? ms : CART_READ_TIMEOUT_MS);
    try {
      return await fetchJson(url, Object.assign({}, options, { signal: controller.signal }));
    } finally {
      clearTimeout(timer);
    }
  }

  // The cart's unit count before an add. _sm-theme's quickCartTotal keeps the
  // global cartObject in step with the drawer on every redraw — after an add, a
  // drawer quantity change, a removal — and renders that same item_count into
  // the cart count, so the baseline can be read as data rather than parsed back
  // out of markup. The rendered count stands in only for a page where that
  // global never initialized. A redraw still running from the previous add
  // hasn't counted it yet, so this waits it out; one that couldn't read the
  // cart left cartObject behind the server, so the count is read fresh instead.
  // Null when no trustworthy count is available.
  async function cartUnitsKnown() {
    await pendingRedraw;
    if (cartObjectStale) {
      var cart = await readCart();
      return cart ? cart.item_count : null;
    }
    var known = window.cartObject && window.cartObject.item_count;
    if (typeof known === 'number') return known;
    var el = document.querySelector('.js__ajax-cart-count');
    var count = el ? parseInt(el.textContent, 10) : NaN;
    return isNaN(count) ? null : count;
  }

  // An add that ends without a status line is unresolved rather than refused:
  // a dropped connection can lose Shopify's answer after the add was applied,
  // and an add still in transit can land after a first read of the cart. So the
  // cart is read, and read once more after a pause, before an add is called
  // failed. Confirmation needs the cart to have grown by the whole quantity
  // over the baseline; with no baseline there is nothing to confirm against.
  // Resolves to null when the add can't be shown to have landed, or when
  // Shopify answered with a status of its own and there is nothing to reconcile.
  async function confirmAddLanded(failure, unitsBefore, qty) {
    if (failureStatus(failure) !== 0 || typeof unitsBefore !== 'number') return null;
    var unitsWanted = unitsBefore + (qty > 0 ? qty : 1);
    for (var attempt = 0; attempt < RECOVERY_READS; attempt++) {
      if (attempt > 0) await wait(RECOVERY_RECHECK_MS);
      var cart = await readCart();
      if (cart && cart.item_count >= unitsWanted) return cart;
    }
    return null;
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  // Reads the cart as the server has it, resolving to null rather than throwing
  // so callers can treat an unreadable cart as nothing to reconcile and leave
  // the UI as it stands.
  async function readCart() {
    try {
      return (await fetchJsonWithTimeout('/cart.js', {}, CART_READ_TIMEOUT_MS)).data;
    } catch (readErr) {
      return null;
    }
  }

  // Pulls the drawer and progress bar back in line with the server's cart after
  // an add Shopify declined, so a quantity or count that had drifted out of step
  // is corrected alongside the message. Mirrors the success path without its
  // confirmation.
  async function resyncCartUi() {
    try {
      if (typeof getglobalLib === 'function' && getglobalLib('Mini_Cart') !== 'yes') return;
      var cart = await readCart();
      if (!cart) return;
      if (typeof progressBar === 'function') progressBar();
      if (typeof reloadAjaxCartItemUsingCartAjaxObject === 'function') {
        await reloadAjaxCartItemUsingCartAjaxObject(cart);
      }
    } catch (resyncErr) {
      console.warn('cart resync failed', resyncErr);
    }
  }

  // Confirms a landed add to the shopper: the success message, the drawer
  // opening and its redraw. The rebuild re-reads the cart itself, so the cart
  // passed here is only its fallback copy, handed over by an add that recovered
  // against one. The redraw is recorded as pending so the next add's baseline
  // waits for it. Swallows its own failures, since a drawer that never redrew
  // doesn't mean the add didn't happen.
  function showAddedToCart(cart) {
    pendingRedraw = redrawAfterAdd(cart).catch(function (redrawErr) {
      console.warn('cart redraw failed', redrawErr);
    });
    return pendingRedraw;
  }

  // quickCartTotal replaces cartObject whenever the rebuild renders a real
  // cart, so a cartObject that is still the same object afterwards means the
  // rebuild fell back to CartJS's copy, which never saw this add.
  async function redrawAfterAdd(cart) {
    var cartObjectBefore = window.cartObject;
    try {
      if (typeof showCartSuccessMessage === 'function') showCartSuccessMessage();
      if (typeof progressBar === 'function') progressBar();
      if (typeof reloadAjaxCartItemUsingCartAjaxObject === 'function') {
        await reloadAjaxCartItemUsingCartAjaxObject(cart);
      }
    } catch (drawerErr) {
      console.warn('cart drawer refresh failed (item was added)', drawerErr);
    }
    cartObjectStale = window.cartObject === cartObjectBefore;
  }

  window.iwAtc = {
    setButtonState: setButtonState,
    guardedAdd: guardedAdd,
    fetchJsonWithTimeout: fetchJsonWithTimeout,
    addBackstopMs: addBackstopMs,
    showFailureMessage: showFailureMessage,
    shopifyAnswered: shopifyAnswered,
    cartUnitsKnown: cartUnitsKnown,
    confirmAddLanded: confirmAddLanded,
    readCart: readCart,
    resyncCartUi: resyncCartUi,
    showAddedToCart: showAddedToCart,
    announce: announce,
  };
})();
